import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const faqs = [
  {
    question: "What is Ayahuasca?",
    answer: "Ayahuasca is a traditional Amazonian plant medicine that has been used for centuries by indigenous peoples for healing, spiritual growth, and connection with nature. It is prepared as a brew from the Banisteriopsis caapi vine and the leaves of the Psychotria viridis plant. The experience is facilitated in a ceremonial setting under the guidance of trained practitioners."
  },
  {
    question: "Is Ayahuasca safe?",
    answer: "When administered in a proper ceremonial setting by qualified facilitators, with appropriate screening and preparation, ayahuasca has been used safely for millennia. We conduct thorough medical and psychological screenings, follow strict dietary protocols, and maintain professional medical support throughout. However, it is not suitable for everyone—we will discuss any contraindications during your consultation."
  },
  {
    question: "How should I prepare for the ceremony?",
    answer: "Preparation involves dietary restrictions (no alcohol, red meat, fermented foods, or recreational substances) for 2 weeks prior. You should avoid certain medications, particularly SSRIs and MAOIs, for a longer period. Mental preparation includes setting clear intentions and being open to the experience. We provide detailed preparation guidelines after booking."
  },
  {
    question: "What is the ceremony experience like?",
    answer: "Each ceremony is unique and deeply personal. The experience typically lasts 4-6 hours and may include visual and emotional journeys, insights, and profound states of consciousness. You will be in a safe space with our facilitators present throughout. Many people describe the experience as life-changing, offering deep healing and self-understanding."
  },
  {
    question: "How do I get to your retreat center?",
    answer: "We are located in the Brazilian Amazon. We provide detailed travel instructions and can arrange airport pickup from Manaus. International guests typically fly into Manaus International Airport (MAO), and we coordinate transportation from there. Full logistical support is provided once you book your retreat."
  },
  {
    question: "What should I bring?",
    answer: "We recommend comfortable, modest clothing in white or light colors for ceremonies, warm layers for evenings, personal toiletries, a journal for integration, and any personal items that hold spiritual significance. We provide accommodation essentials, ceremonial supplies, and all meals during your stay."
  },
  {
    question: "What is integration and why is it important?",
    answer: "Integration is the process of making sense of your ceremony experiences and applying insights to your daily life. It's perhaps the most important part of the journey. We provide integration sessions during your stay and offer ongoing support afterward. Many of the benefits of ayahuasca come through thoughtful integration work."
  },
  {
    question: "Can I attend alone or should I bring someone?",
    answer: "Both options are valid. Many guests come alone and find deep connection with other participants. Bringing a trusted friend or partner can also be supportive. We maintain small group sizes to ensure everyone receives personalized attention regardless of whether you come alone or with others."
  }
];

export const FAQSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="faq" className="py-24 relative" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-neon-cyan text-sm tracking-[0.3em] mb-4">
            YOUR QUESTIONS ANSWERED
          </p>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight text-white mb-6">
            Frequently <span className="neon-gold">Asked</span>
          </h2>
          <p className="font-body text-gray-400 text-lg">
            Everything you need to know before embarking on your sacred journey.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="glass-card rounded-xl border border-egyptian-gold/20 px-6 overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="font-heading text-lg text-white uppercase tracking-wide hover:text-egyptian-gold hover:no-underline py-6 [&[data-state=open]>svg]:text-egyptian-gold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-body text-gray-400 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="font-body text-gray-400 mb-4">
            Still have questions? We're here to help.
          </p>
          <a
            href="https://wa.me/5521972232170"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-egyptian-gold font-heading uppercase tracking-wider hover:text-neon-cyan transition-colors duration-300"
            data-testid="faq-contact-link"
          >
            Contact us on WhatsApp
            <span className="text-xl">→</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
